/**
 * API errors, turned into something a screen can act on.
 *
 * The backend answers every failure with the same envelope —
 * `{ error: { code, message, details, requestId } }` — and that shape is the
 * whole reason one error class can serve forty modules. What it does not do is
 * decide what a screen should say: `PAYROLL_PERIOD_LOCKED` is precise and
 * unreadable, and a person looking at a locked period needs to be told what to
 * do about it.
 *
 * So the code is preserved exactly and a human sentence is attached beside it.
 * The rule is that the message names the thing that happened and, where there
 * is one, the way out. "Something went wrong" is never a correct answer to an
 * error the backend was specific about.
 *
 * The request id travels with the error so a support conversation can start
 * with a log line rather than a description of a screen.
 */

export type ApiErrorCategory =
  | 'authentication'
  | 'authorization'
  | 'validation'
  | 'not_found'
  | 'conflict'
  | 'invalid_transition'
  | 'idempotency'
  | 'rate_limited'
  | 'upload'
  | 'offline'
  | 'service_unavailable'
  | 'session_expired'
  | 'organization_inactive'
  | 'unknown';

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly category: ApiErrorCategory;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;
  /** What the backend said, kept for diagnostics rather than for display. */
  readonly serverMessage: string;

  constructor(input: {
    code: string;
    status: number;
    message: string;
    serverMessage?: string;
    category: ApiErrorCategory;
    details?: Record<string, unknown>;
    requestId?: string | null;
  }) {
    super(input.message);
    this.name = 'ApiError';
    this.code = input.code;
    this.status = input.status;
    this.category = input.category;
    this.details = input.details ?? {};
    this.requestId = input.requestId ?? null;
    this.serverMessage = input.serverMessage ?? input.message;
  }

  /** Field errors from a rejected form, keyed the way the form is keyed. */
  get fieldErrors(): Record<string, string> {
    const issues = this.details.issues;
    if (!Array.isArray(issues)) return {};

    const mapped: Record<string, string> = {};
    for (const issue of issues) {
      const entry = issue as { path?: unknown; message?: unknown };
      if (typeof entry.path === 'string' && typeof entry.message === 'string') {
        mapped[entry.path] = entry.message;
      }
    }
    return mapped;
  }

  /**
   * Whether repeating the request unchanged could succeed.
   *
   * A validation failure, a permission denial and an invalid transition will
   * fail identically forever; offering Retry on them wastes the reader's time
   * and hides the actual fix.
   */
  get retryable(): boolean {
    return (
      this.category === 'offline' ||
      this.category === 'service_unavailable' ||
      this.category === 'rate_limited'
    );
  }
}

function categoryFor(status: number, code: string): ApiErrorCategory {
  if (
    code === 'SESSION_EXPIRED' ||
    code === 'SESSION_IDLE_TIMEOUT' ||
    code === 'REFRESH_TOKEN_REUSE_DETECTED'
  ) {
    return 'session_expired';
  }
  if (code === 'ORGANIZATION_INACTIVE') return 'organization_inactive';
  if (code === 'IDEMPOTENCY_KEY_REUSED' || code === 'IDEMPOTENT_REQUEST_IN_FLIGHT') {
    return 'idempotency';
  }
  if (code.startsWith('UPLOAD_') || code.startsWith('MEDIA_') || code.startsWith('STORAGE_')) {
    return 'upload';
  }
  if (code.endsWith('_INVALID_TRANSITION') || code === 'ATTENDANCE_INVALID_SEQUENCE') {
    return 'invalid_transition';
  }

  switch (status) {
    case 401:
      return 'authentication';
    case 403:
      return 'authorization';
    case 400:
      return 'validation';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 422:
      return 'invalid_transition';
    case 429:
      return 'rate_limited';
    case 502:
    case 503:
    case 504:
      return 'service_unavailable';
    default:
      return 'unknown';
  }
}

/**
 * Sentences for the codes a person actually meets.
 *
 * Only codes whose backend wording would confuse somebody are overridden. The
 * backend's own messages are written for people and are usually better than a
 * generic restatement — `errors.rule` messages in particular already say what
 * to do. Anything not listed falls through to the server's message.
 */
const MESSAGES: Record<string, string> = {
  ATTENDANCE_INVALID_SEQUENCE: 'That does not follow from your current attendance state.',
  ATTENDANCE_ALREADY_TIMED_IN: 'Your Time In was already recorded.',
  ATTENDANCE_NO_ACTIVE_BREAK: 'You do not have a break running.',
  PAYROLL_PERIOD_LOCKED:
    'This payroll period is locked. Reopen it with the required permission before making changes.',
  PAYROLL_INVALID_TRANSITION: 'This payroll period cannot move to that state from where it is now.',
  PAYSLIP_ALREADY_PUBLISHED: 'Payslips for this period have already been published.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  APPROVAL_WRONG_STEP: 'This request is waiting on an earlier approval step.',
  APPROVAL_ALREADY_DECIDED: 'This request has already been decided.',
  APPROVAL_COMMENT_REQUIRED: 'A reason is required for this decision.',
  STALE_REVISION: 'This record changed after you opened it.',
  SESSION_EXPIRED: 'Your session has ended. Sign in again to continue.',
  RATE_LIMITED: 'Too many requests. Wait a moment and try again.',
  MALFORMED_REQUEST: 'The request could not be read. This is a bug — please report it.',
  INTERNAL_ERROR: 'Something went wrong on our side. The request id below will help support.',
  NETWORK_ERROR: 'Could not reach the server. Check your connection and try again.',
  TIMEOUT: 'The server took too long to answer. Try again.',
};

export function toApiError(input: {
  status: number;
  body: unknown;
  requestId?: string | null;
}): ApiError {
  const envelope = (input.body as { error?: ApiErrorBody } | null)?.error;

  const code = envelope?.code ?? (input.status === 0 ? 'NETWORK_ERROR' : 'UNKNOWN_ERROR');
  const serverMessage = envelope?.message ?? 'The request failed.';

  return new ApiError({
    code,
    status: input.status,
    // The server's own wording wins unless we have a better one, because it is
    // written against the specific rule that failed.
    message: MESSAGES[code] ?? serverMessage,
    serverMessage,
    category: categoryFor(input.status, code),
    details: envelope?.details ?? {},
    requestId: envelope?.requestId ?? input.requestId ?? null,
  });
}

/** A failure that never reached the server. */
export function networkError(cause: unknown): ApiError {
  const timedOut = cause instanceof Error && cause.name === 'AbortError';

  return new ApiError({
    code: timedOut ? 'TIMEOUT' : 'NETWORK_ERROR',
    status: 0,
    message: MESSAGES[timedOut ? 'TIMEOUT' : 'NETWORK_ERROR']!,
    category: 'offline',
    details: {},
  });
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
